/**
 * The page's notice lines in the editor that its keyed groups (`editor`) do not hold: what the status bar says after
 * a key command, a fill, a press on a hidden or locked layer, a select press, a template, a rollback and a save or a
 * publish that failed. Each is a draft for the author unless the code that asks for it says otherwise. Sentence
 * case, with no full stop, since a notice line is read in the status bar.
 *
 * `{layer}` and `{group}` are names a builder gave; `{name}` is a template's name; `{reason}` is what a server, the
 * browser or a check said, shown as it came; `{words}` in `mergeFailed` is a pull failure's sentence, which ends with
 * its own full stop. `{count}` is a number as the page prints it, and `{noun}` is `selection.prop`, `selection.props`,
 * `selection.marker` or `selection.markers`. `{what}` in `fill.filled` is `fill.corners` or `fill.cells`, `{kind}` in `fill.unsupportedKind`
 * is `fill.aProp` or `fill.aMarker`, and `{mode}` in `select.groundUnder` is `select.modeTiles` or `select.modeTerrain`.
 * `{direction}` is one of `keys.directions` and `{tiles}` a number of tiles.
 */
export const EDITOR_NOTICE = {
  marker: {
    offMap: "A marker has to stay on the map",
    droppedCells: { one: "{count} painted cell fell outside the map or the marker's reach and was left out", other: "{count} painted cells fell outside the map or the marker's reach and were left out" },
  },
  area: {
    putCopyDown: "Put the copy down",
    putAreaDown: "Put the area down",
    pickUpCopy: "Pick up the copy",
    pickUpArea: "Pick up the area",
    selectFirst: "Select an area first: the Select tool (V), All layers on the tool strip, then drag a box",
    stepFar: "Step the pending area {direction} {tiles} tiles",
    noMove: "No area's move is pending; pick an area up with M first",
  },
  brush: {
    stoppedAt: "Stopped at {count} props",
  },
  fill: {
    filled: "Filled {count} {what} of {layer}",
    corners: "corners",
    cells: "cells",
    unsupported: "Fill works on a terrain, tile, collision or zone layer; choose one first",
    unsupportedKind: "Fill works on terrain, tile, collision and zone layers, not on {kind} layer like {layer}",
    aProp: "a prop",
    aMarker: "a marker",
    chooseTile: "Choose a tile in the palette to fill with first",
    noZoneType: "The game registers no zone type to fill with",
    noCollisionBrush: "The game declares no collision brush to fill with",
  },
  hidden: {
    alone: "{layer} is hidden and takes no edit; show it to edit it",
    both: "{layer} and its group {group} are hidden, so {layer} takes no edit; show both to edit it",
    withGroup: "{layer} is hidden with its group {group} and takes no edit; show the group to edit it",
  },
  locked: {
    alone: "{layer} is locked: unlock it on its row to edit it",
    both: "{layer} and its group {group} are locked: unlock both on their rows to edit it",
    withGroup: "{layer} is locked with its group {group}: unlock the group on its row to edit it",
  },
  lookup: {
    broken: "Broken: {id}",
    failsChecks: "fails its checks ({reason})",
  },
  save: {
    recompileFailed: "Save and recompile failed: {reason}",
    publishRefused: "Publish refused: {reason}",
    mergeFailed: "Their changes could not be brought in: {words} Your changes are still in this tab.",
    byThemselves: "Their changes could not be brought in by themselves: {reason}",
  },
  reference: {
    nothingToPick: "Nothing to pick here; click a prop or a marker, or press Escape to stop picking",
  },
  select: {
    onlyBase: "Only the base is here, and it is locked",
    groundUnder: "{layer} is under the pointer; choose {mode} to edit it",
    modeTiles: "Tiles",
    modeTerrain: "Terrain",
  },
  selection: {
    needProps: "Select props first; Ctrl+D then takes them in hand as one group",
    wholeOf: "Selected the whole of {layer}",
    holdsNone: "{layer} holds no {noun} to select",
    selected: "Selected {count} {noun} on {layer}",
    prop: "prop",
    props: "props",
    marker: "marker",
    markers: "markers",
    inHand: "{name} in hand: click to place it, Esc to put it down",
    group: "Group",
    notTaken: "Not taken in hand: {reason}",
    selectAll: "Select all",
    duplicate: "Duplicate the selected props to hand",
  },
  standIn: {
    unanswered: "{url} did not answer ({reason})",
    responded: "{url} responded {status}",
    notJson: "{url} is not JSON",
  },
  template: {
    notPlaced: "{name} not placed: {reason}",
    cannotShow: "{name} cannot be shown: {reason}",
  },
  version: {
    publishRunning: "A publish is running",
    rollbackRunning: "A rollback is running",
    recompileRunning: "A recompile is running",
    rollbackRefused: "Rollback refused: {reason}",
    rolledBack: "Rolled back to {version}; players reload into it",
  },
} as const;
